import {
  useCallback,
  useLayoutEffect,
  useRef,
  type KeyboardEventHandler,
  type PointerEventHandler,
  type RefObject,
  type TouchEventHandler,
  type UIEventHandler,
  type WheelEventHandler,
} from 'react';

import { prefersReducedMotion } from '@/lib/motion';

/** TP follows new content only while the reader is within this distance of the bottom (OnboardingV2Page: `< 160`). */
export const PIN_DISTANCE_PX = 160;

/**
 * How long a reader's gesture (a wheel notch, a touch move, a key, a press on the scrollbar) counts as "the reader is
 * steering" for the scroll events that follow it. Each scroll event inside the window extends it, so a long drag or
 * a fling keeps counting; a scroll event outside it is ours (or the layout's) and never lets go of the bottom.
 */
export const INTENT_WINDOW_MS = 500;

export interface StickToBottomOptions {
  /** The reader counts as "at the bottom" within this many px (default `PIN_DISTANCE_PX`). */
  pinDistancePx?: number;
}

/** What to spread onto the scrolling element, next to `ref={listRef}`. */
export interface StickToBottomHandlers<T extends HTMLElement> {
  onScroll: UIEventHandler<T>;
  onWheel: WheelEventHandler<T>;
  onTouchMove: TouchEventHandler<T>;
  onKeyDown: KeyboardEventHandler<T>;
  onPointerDown: PointerEventHandler<T>;
}

export interface StickToBottom<T extends HTMLElement> {
  /** Attach to the scrolling list. */
  listRef: RefObject<T>;
  /** Spread onto the same element: the scroll and the reader's own gestures. */
  scrollerProps: StickToBottomHandlers<T>;
  /** Scroll to the bottom now, but only while the reader is pinned (near the bottom). */
  scrollToBottomIfPinned: () => void;
}

/** Keys that move the reader up (PageUp, ArrowUp, Home, Shift+Space). Down keys follow the content anyway. */
const isScrollUpKey = (event: { key: string; shiftKey: boolean }): boolean =>
  event.key === 'PageUp' || event.key === 'ArrowUp' || event.key === 'Home' || (event.key === ' ' && event.shiftKey);

/**
 * Keeps a scrolling list at its bottom as it grows, until the reader scrolls away (TP: a 160px threshold, a layout
 * effect, a smooth scroll; instant under reduced motion). `scrollKey` asks for a scroll whenever it changes (a list
 * that also follows its own growth, with a ResizeObserver, can leave it out). Pinned by default, so the first render
 * lands on the latest line.
 *
 * The reader leaves the bottom only by doing something: a wheel up, a touch move, an up key, or a press on the
 * scroller itself (its scrollbar), followed by a scroll that ends beyond the pin distance. A scroll event nobody
 * asked for (our own smooth scroll still on its way while a card grows under it, the browser re-anchoring) never
 * un-pins, however far from the new bottom it reads. Coming within the pin distance always pins again.
 * One hook serves every follow-the-newest list (the chat thread and the ads log).
 */
export function useStickToBottom<T extends HTMLElement>(
  scrollKey: string | number | undefined,
  { pinDistancePx = PIN_DISTANCE_PX }: StickToBottomOptions = {},
): StickToBottom<T> {
  const listRef = useRef<T>(null);
  const pinnedRef = useRef(true);
  /** Until when (epoch ms) the reader counts as steering; 0 = not at all. */
  const steeringUntilRef = useRef(0);

  const steer = useCallback(() => {
    steeringUntilRef.current = Date.now() + INTENT_WINDOW_MS;
  }, []);

  const scrollToBottomIfPinned = useCallback(() => {
    const el = listRef.current;
    if (!el || !pinnedRef.current) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } else el.scrollTop = el.scrollHeight;
  }, []);

  useLayoutEffect(() => {
    scrollToBottomIfPinned();
  }, [scrollKey, scrollToBottomIfPinned]);

  const onScroll = useCallback<UIEventHandler<T>>(() => {
    const el = listRef.current;
    if (!el) return;
    const steering = Date.now() < steeringUntilRef.current;
    // A gesture that is still moving the list keeps its window open (a slow drag, a fling).
    if (steering) steer();
    if (el.scrollHeight - el.scrollTop - el.clientHeight < pinDistancePx) {
      pinnedRef.current = true;
      return;
    }
    if (steering) pinnedRef.current = false;
  }, [pinDistancePx, steer]);

  const onWheel = useCallback<WheelEventHandler<T>>(
    (event) => {
      if (event.deltaY < 0) steer();
    },
    [steer],
  );
  const onTouchMove = useCallback<TouchEventHandler<T>>(() => steer(), [steer]);
  const onKeyDown = useCallback<KeyboardEventHandler<T>>(
    (event) => {
      if (isScrollUpKey(event)) steer();
    },
    [steer],
  );
  // Only a press on the scroller itself (its scrollbar, its padding): a press on a card inside it is a click, not a scroll.
  const onPointerDown = useCallback<PointerEventHandler<T>>(
    (event) => {
      if (event.target === event.currentTarget) steer();
    },
    [steer],
  );

  return { listRef, scrollerProps: { onScroll, onWheel, onTouchMove, onKeyDown, onPointerDown }, scrollToBottomIfPinned };
}
